import { useState, useEffect, useRef, useCallback } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMapEvents, useMap } from 'react-leaflet';
import L from 'leaflet';
import { useNodes } from '../hooks/useNodes';
import { geolocateIP, login as apiLogin } from '../api/nodes';
import Login from '../components/Login';

// Fix for default marker icons
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
});

// Tab component for different add methods
function AddNodeTabs({ activeTab, onTabChange }) {
  const tabs = [
    { id: 'manual', label: 'Manual' },
    { id: 'ip', label: 'From IP' },
    { id: 'map', label: 'Click Map' },
    { id: 'import', label: 'Import JSON' }
  ];

  return (
    <div className="tabs">
      {tabs.map(tab => (
        <button
          key={tab.id}
          className={`tab ${activeTab === tab.id ? 'active' : ''}`}
          onClick={() => onTabChange(tab.id)}
        >
          {tab.label}
        </button>
      ))}
    </div>
  );
}

// Manual form for adding nodes
function ManualForm({ onSubmit, initialData, isEditing }) {
  const [formData, setFormData] = useState({
    name: '',
    lat: '',
    lng: '',
    city: '',
    country: '',
    comment: '',
    metadata: '{}'
  });

  useEffect(() => {
    if (initialData) {
      setFormData({
        name: initialData.name || '',
        lat: initialData.coordinate?.lat?.toString() || '',
        lng: initialData.coordinate?.lng?.toString() || '',
        city: initialData.city || '',
        country: initialData.country || '',
        comment: initialData.comment || '',
        metadata: JSON.stringify(initialData.metadata || {}, null, 2)
      });
    }
  }, [initialData]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    let metadata = {};
    try {
      metadata = JSON.parse(formData.metadata || '{}');
    } catch (err) {
      alert('Invalid JSON in metadata field');
      return;
    }

    onSubmit({
      name: formData.name,
      coordinate: {
        lat: parseFloat(formData.lat),
        lng: parseFloat(formData.lng)
      },
      city: formData.city,
      country: formData.country,
      comment: formData.comment,
      metadata
    });

    if (!isEditing) {
      setFormData({
        name: '',
        lat: '',
        lng: '',
        city: '',
        country: '',
        comment: '',
        metadata: '{}'
      });
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <div className="form-group">
        <label>Name *</label>
        <input
          type="text"
          name="name"
          value={formData.name}
          onChange={handleChange}
          required
          placeholder="Unique node name"
        />
      </div>
      <div className="form-row">
        <div className="form-group">
          <label>Latitude *</label>
          <input
            type="number"
            name="lat"
            value={formData.lat}
            onChange={handleChange}
            required
            step="any"
            placeholder="-90 to 90"
          />
        </div>
        <div className="form-group">
          <label>Longitude *</label>
          <input
            type="number"
            name="lng"
            value={formData.lng}
            onChange={handleChange}
            required
            step="any"
            placeholder="-180 to 180"
          />
        </div>
      </div>
      <div className="form-row">
        <div className="form-group">
          <label>City</label>
          <input
            type="text"
            name="city"
            value={formData.city}
            onChange={handleChange}
            placeholder="City name"
          />
        </div>
        <div className="form-group">
          <label>Country</label>
          <input
            type="text"
            name="country"
            value={formData.country}
            onChange={handleChange}
            placeholder="Country name"
          />
        </div>
      </div>
      <div className="form-group">
        <label>Comment</label>
        <textarea
          name="comment"
          value={formData.comment}
          onChange={handleChange}
          rows={2}
          placeholder="Optional comment"
        />
      </div>
      <div className="form-group">
        <label>Metadata (JSON)</label>
        <textarea
          name="metadata"
          value={formData.metadata}
          onChange={handleChange}
          rows={3}
          placeholder='{"key": "value"}'
        />
      </div>
      <button type="submit" className="btn btn-primary">
        {isEditing ? 'Update Node' : 'Add Node'}
      </button>
    </form>
  );
}

// IP geolocation form
function IPForm({ onSubmit }) {
  const [ip, setIp] = useState('');
  const [loading, setLoading] = useState(false);
  const [geoData, setGeoData] = useState(null);
  const [error, setError] = useState(null);
  const [customName, setCustomName] = useState('');
  const [comment, setComment] = useState('');

  const handleLookup = async () => {
    if (!ip) return;

    setLoading(true);
    setError(null);
    setGeoData(null);

    try {
      const data = await geolocateIP(ip);
      setGeoData(data);
      setCustomName(ip);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!geoData) return;

    onSubmit({
      name: customName || ip,
      coordinate: geoData.coordinate,
      city: geoData.city,
      country: geoData.country,
      comment: comment,
      metadata: {
        ip: geoData.ip,
        isp: geoData.isp,
        org: geoData.org,
        timezone: geoData.timezone,
        region: geoData.region
      }
    });

    setIp('');
    setGeoData(null);
    setCustomName('');
    setComment('');
  };

  return (
    <div>
      <div className="form-group">
        <label>IP Address</label>
        <div style={{ display: 'flex', gap: '10px' }}>
          <input
            type="text"
            value={ip}
            onChange={(e) => setIp(e.target.value)}
            placeholder="e.g., 8.8.8.8"
            style={{ flex: 1 }}
          />
          <button
            type="button"
            className="btn btn-secondary"
            onClick={handleLookup}
            disabled={loading || !ip}
          >
            {loading ? 'Looking up...' : 'Lookup'}
          </button>
        </div>
      </div>

      {error && <div className="message error">{error}</div>}

      {geoData && (
        <form onSubmit={handleSubmit}>
          <div className="message success">
            Found location: {geoData.city}, {geoData.country} ({geoData.coordinate.lat}, {geoData.coordinate.lng})
          </div>
          <div className="form-group">
            <label>Node Name</label>
            <input
              type="text"
              value={customName}
              onChange={(e) => setCustomName(e.target.value)}
              placeholder="Node name (defaults to IP)"
            />
          </div>
          <div className="form-group">
            <label>Comment</label>
            <textarea
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              rows={2}
              placeholder="Optional comment"
            />
          </div>
          <button type="submit" className="btn btn-primary">
            Add Node
          </button>
        </form>
      )}
    </div>
  );
}

// Map click handler
function MapClickHandler({ onMapClick, enabled }) {
  useMapEvents({
    click: (e) => {
      if (enabled) {
        onMapClick(e.latlng);
      }
    }
  });
  return null;
}

// Map form for clicking to add
function MapForm({ onSubmit }) {
  const [clickedLocation, setClickedLocation] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    city: '',
    country: '',
    comment: ''
  });

  const handleMapClick = (latlng) => {
    setClickedLocation(latlng);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!clickedLocation) {
      alert('Please click on the map to select a location');
      return;
    }

    onSubmit({
      name: formData.name,
      coordinate: {
        lat: clickedLocation.lat,
        lng: clickedLocation.lng
      },
      city: formData.city,
      country: formData.country,
      comment: formData.comment,
      metadata: {}
    });

    setClickedLocation(null);
    setFormData({
      name: '',
      city: '',
      country: '',
      comment: ''
    });
  };

  return (
    <div>
      <p style={{ marginBottom: '10px', color: '#7f8c8d' }}>
        Click on the map below to select a location for the new node.
      </p>
      <div className="admin-map" style={{ marginBottom: '15px' }}>
        <MapContainer
          center={[20, 0]}
          zoom={2}
          style={{ height: '100%', width: '100%' }}
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          <MapClickHandler onMapClick={handleMapClick} enabled={true} />
          {clickedLocation && (
            <Marker position={[clickedLocation.lat, clickedLocation.lng]}>
              <Popup>
                Selected: {clickedLocation.lat.toFixed(6)}, {clickedLocation.lng.toFixed(6)}
              </Popup>
            </Marker>
          )}
        </MapContainer>
      </div>

      {clickedLocation && (
        <div className="message info">
          Selected: {clickedLocation.lat.toFixed(6)}, {clickedLocation.lng.toFixed(6)}
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <div className="form-group">
          <label>Name *</label>
          <input
            type="text"
            name="name"
            value={formData.name}
            onChange={handleChange}
            required
            placeholder="Unique node name"
          />
        </div>
        <div className="form-row">
          <div className="form-group">
            <label>City</label>
            <input
              type="text"
              name="city"
              value={formData.city}
              onChange={handleChange}
              placeholder="City name"
            />
          </div>
          <div className="form-group">
            <label>Country</label>
            <input
              type="text"
              name="country"
              value={formData.country}
              onChange={handleChange}
              placeholder="Country name"
            />
          </div>
        </div>
        <div className="form-group">
          <label>Comment</label>
          <textarea
            name="comment"
            value={formData.comment}
            onChange={handleChange}
            rows={2}
            placeholder="Optional comment"
          />
        </div>
        <button type="submit" className="btn btn-primary" disabled={!clickedLocation}>
          Add Node
        </button>
      </form>
    </div>
  );
}

// Import JSON form
function ImportForm({ onImport }) {
  const [dragOver, setDragOver] = useState(false);
  const [importing, setImporting] = useState(false);
  const fileInputRef = useRef(null);

  const handleFileSelect = async (file) => {
    if (!file) return;

    setImporting(true);
    try {
      await onImport(file);
    } finally {
      setImporting(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file && file.type === 'application/json') {
      handleFileSelect(file);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = () => {
    setDragOver(false);
  };

  return (
    <div>
      <div
        className={`file-upload ${dragOver ? 'drag-over' : ''}`}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onClick={() => fileInputRef.current?.click()}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept=".json"
          onChange={(e) => handleFileSelect(e.target.files[0])}
        />
        {importing ? (
          <p>Importing...</p>
        ) : (
          <>
            <p>Drop a JSON file here or click to select</p>
            <p style={{ fontSize: '12px', color: '#95a5a6' }}>
              Format: {"[{name, coordinate: {lat, lng}, city, country, comment, metadata}, ...]"}
            </p>
          </>
        )}
      </div>
      <div style={{ marginTop: '15px' }}>
        <h4>Example JSON format:</h4>
        <pre style={{ background: '#f8f9fa', padding: '10px', borderRadius: '4px', fontSize: '12px', overflow: 'auto' }}>
{`[
  {
    "name": "Node 1",
    "coordinate": { "lat": 40.7128, "lng": -74.0060 },
    "city": "New York",
    "country": "USA",
    "comment": "Example node",
    "metadata": { "type": "server" }
  }
]`}
        </pre>
      </div>
    </div>
  );
}

// Node list item
function NodeListItem({ node, onEdit, onDelete, onLocate }) {
  return (
    <div className="node-item">
      <div className="node-item-info">
        <div className="node-item-name">{node.name}</div>
        <div className="node-item-location">
          {node.city && node.country
            ? `${node.city}, ${node.country}`
            : node.city || node.country || `${node.coordinate.lat.toFixed(4)}, ${node.coordinate.lng.toFixed(4)}`}
        </div>
      </div>
      <div className="node-item-actions">
        <button
          className="btn btn-secondary btn-small"
          onClick={() => onLocate(node)}
          title="Show on map"
        >
          Locate
        </button>
        <button
          className="btn btn-primary btn-small"
          onClick={() => onEdit(node)}
          title="Edit node"
        >
          Edit
        </button>
        <button
          className="btn btn-danger btn-small"
          onClick={() => onDelete(node)}
          title="Delete node"
        >
          Delete
        </button>
      </div>
    </div>
  );
}

// Edit modal
function EditModal({ node, onSave, onClose }) {
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Edit Node</h2>
          <button className="modal-close" onClick={onClose}>&times;</button>
        </div>
        <div className="modal-body">
          <ManualForm
            onSubmit={onSave}
            initialData={node}
            isEditing={true}
          />
        </div>
      </div>
    </div>
  );
}

// Map view of all nodes
function AdminMapView({ nodes, selectedNode }) {
  const MapController = () => {
    const map = useMap();

    useEffect(() => {
      if (selectedNode) {
        map.flyTo(
          [selectedNode.coordinate.lat, selectedNode.coordinate.lng],
          14,
          { duration: 1 }
        );
      }
    }, [map, selectedNode]);

    return null;
  };

  return (
    <div className="admin-map">
      <MapContainer
        center={[20, 0]}
        zoom={2}
        style={{ height: '100%', width: '100%' }}
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        <MapController selectedNode={selectedNode} />
        {nodes.map(node => (
          <Marker
            key={node.id}
            position={[node.coordinate.lat, node.coordinate.lng]}
          >
            <Popup>
              <strong>{node.name}</strong>
              <br />
              {node.city && node.country
                ? `${node.city}, ${node.country}`
                : node.city || node.country || 'Unknown location'}
            </Popup>
          </Marker>
        ))}
      </MapContainer>
    </div>
  );
}

export default function AdminPage() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  const {
    nodes,
    loading,
    error,
    addNode,
    editNode,
    removeNode,
    removeAllNodes,
    importFromFile,
    loadNodes
  } = useNodes();

  const [activeTab, setActiveTab] = useState('manual');
  const [searchQuery, setSearchQuery] = useState('');
  const [editingNode, setEditingNode] = useState(null);
  const [locatedNode, setLocatedNode] = useState(null);
  const [message, setMessage] = useState(null);

  // Check for existing auth token on mount
  useEffect(() => {
    const token = localStorage.getItem('adminToken');
    if (token) {
      setIsAuthenticated(true);
    }
  }, []);

  const showMessage = (type, text) => {
    setMessage({ type, text });
    setTimeout(() => setMessage(null), 5000);
  };

  const handleLogin = async (password) => {
    const token = await apiLogin(password);
    localStorage.setItem('adminToken', token);
    setIsAuthenticated(true);
  };

  const handleLogout = () => {
    localStorage.removeItem('adminToken');
    setIsAuthenticated(false);
  };

  const handleAddNode = async (nodeData) => {
    try {
      await addNode(nodeData);
      showMessage('success', `Node "${nodeData.name}" added successfully`);
    } catch (err) {
      showMessage('error', err.message);
    }
  };

  const handleEditNode = async (nodeData) => {
    try {
      await editNode(editingNode.id, nodeData);
      setEditingNode(null);
      showMessage('success', `Node "${nodeData.name}" updated successfully`);
    } catch (err) {
      showMessage('error', err.message);
    }
  };

  const handleDeleteNode = async (node) => {
    if (!confirm(`Are you sure you want to delete "${node.name}"?`)) return;

    try {
      await removeNode(node.id);
      showMessage('success', `Node "${node.name}" deleted`);
    } catch (err) {
      showMessage('error', err.message);
    }
  };

  const handleDeleteAll = async () => {
    if (!confirm('Are you sure you want to delete ALL nodes? This cannot be undone.')) return;
    if (!confirm('Really delete all nodes?')) return;

    try {
      await removeAllNodes();
      showMessage('success', 'All nodes deleted');
    } catch (err) {
      showMessage('error', err.message);
    }
  };

  const handleImport = async (file) => {
    try {
      const result = await importFromFile(file);
      showMessage(
        'success',
        `Imported ${result.success.length} nodes. ${result.failed.length} failed.`
      );
    } catch (err) {
      showMessage('error', err.message);
    }
  };

  const handleSearch = () => {
    loadNodes(searchQuery);
  };

  const handleLocate = (node) => {
    setLocatedNode(node);
  };

  const filteredNodes = searchQuery
    ? nodes.filter(node =>
        node.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        node.city.toLowerCase().includes(searchQuery.toLowerCase()) ||
        node.country.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : nodes;

  // Show login if not authenticated
  if (!isAuthenticated) {
    return <Login onLogin={handleLogin} />;
  }

  if (loading && nodes.length === 0) {
    return (
      <div className="admin-page">
        <div className="loading">
          <div className="loading-spinner"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-page">
      <div className="admin-header">
        <h1>Node Management</h1>
        <div className="admin-stats">
          <div className="stat-card">
            <div className="stat-value">{nodes.length}</div>
            <div className="stat-label">Total Nodes</div>
          </div>
          <button
            className="btn btn-secondary btn-small"
            onClick={handleLogout}
            style={{ marginLeft: '15px' }}
          >
            Logout
          </button>
        </div>
      </div>

      {message && (
        <div className={`message ${message.type}`}>{message.text}</div>
      )}

      {error && (
        <div className="message error">{error}</div>
      )}

      <div className="admin-content">
        {/* Add Node Panel */}
        <div className="admin-panel">
          <div className="panel-header">
            <h2>Add Node</h2>
          </div>
          <div className="panel-body">
            <AddNodeTabs activeTab={activeTab} onTabChange={setActiveTab} />

            {activeTab === 'manual' && (
              <ManualForm onSubmit={handleAddNode} />
            )}

            {activeTab === 'ip' && (
              <IPForm onSubmit={handleAddNode} />
            )}

            {activeTab === 'map' && (
              <MapForm onSubmit={handleAddNode} />
            )}

            {activeTab === 'import' && (
              <ImportForm onImport={handleImport} />
            )}
          </div>
        </div>

        {/* Node List Panel */}
        <div className="admin-panel">
          <div className="panel-header">
            <h2>Nodes List</h2>
            {nodes.length > 0 && (
              <button
                className="btn btn-danger btn-small"
                onClick={handleDeleteAll}
              >
                Delete All
              </button>
            )}
          </div>
          <div className="panel-body">
            <div className="search-box">
              <input
                type="text"
                placeholder="Search nodes..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
              />
              <button className="btn btn-secondary" onClick={handleSearch}>
                Search
              </button>
            </div>

            {filteredNodes.length === 0 ? (
              <div className="empty-state">
                <p>No nodes found</p>
                {searchQuery && <p>Try a different search term</p>}
              </div>
            ) : (
              <div className="node-list">
                {filteredNodes.map(node => (
                  <NodeListItem
                    key={node.id}
                    node={node}
                    onEdit={setEditingNode}
                    onDelete={handleDeleteNode}
                    onLocate={handleLocate}
                  />
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Map View Panel */}
        <div className="admin-panel" style={{ gridColumn: '1 / -1' }}>
          <div className="panel-header">
            <h2>Map View</h2>
          </div>
          <div className="panel-body">
            <AdminMapView nodes={nodes} selectedNode={locatedNode} />
          </div>
        </div>
      </div>

      {/* Edit Modal */}
      {editingNode && (
        <EditModal
          node={editingNode}
          onSave={handleEditNode}
          onClose={() => setEditingNode(null)}
        />
      )}
    </div>
  );
}
